import React from "react";
import { Link } from "react-router-dom";
import { EmergencyBanner, SectionEyebrow } from "@/components/shared/Common";
import { Sparkles, Stethoscope, Shield, CalendarCheck, Video, FolderHeart, ArrowRight, Check, Users, HeartPulse, MessageCircle, CircleDollarSign, FileText, Clock, ChevronRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxmcmllbmRseSUyMGRvY3RvciUyMGNvbnN1bHRpbmclMjBwYXRpZW50JTIwd2FybSUyMGxpZ2h0fGVufDB8fHx8MTc4OTkzNTYzOXww&ixlib=rb-4.1.0&q=85";

const CONSULT_IMG = "https://images.pexels.com/photos/39192383/pexels-photo-39192383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Feature = ({ icon, title, desc, testid }) => (
  <div className="cm-card group" data-testid={testid}>
    <div className="w-10 h-10 rounded-lg bg-[#ebf3fc] text-[#4a90d9] flex items-center justify-center mb-4 group-hover:bg-[#4a90d9] group-hover:text-white transition-colors">{icon}</div>
    <h3 className="text-lg font-semibold text-[#1a2b4a] mb-1.5">{title}</h3>
    <p className="text-sm text-[#475569] leading-relaxed">{desc}</p>
  </div>
);

const Step = ({ n, title, desc }) => (
  <div className="relative pl-14">
    <div className="absolute left-0 top-0 w-10 h-10 rounded-full bg-[#1a2b4a] text-white text-sm font-bold flex items-center justify-center">{n}</div>
    <h3 className="text-lg font-semibold text-[#1a2b4a] mb-1">{title}</h3>
    <p className="text-sm text-[#475569] leading-relaxed">{desc}</p>
  </div>
);

const FAQItem = ({ q, a, idx }) => (
  <details className="cm-card cursor-pointer" data-testid={`faq-item-${idx}`}>
    <summary className="flex items-center justify-between font-semibold text-[#1a2b4a] list-none">
      {q}
      <ChevronRight size={18} className="transition-transform group-open:rotate-90"/>
    </summary>
    <p className="mt-3 text-sm text-[#475569] leading-relaxed">{a}</p>
  </details>
);

export default function Home() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-[#f5f7fa]">
        <div className="cm-container pt-14 pb-16 lg:pt-24 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 cm-fade-up">
            <div className="cm-eyebrow mb-4">The right care, made simpler</div>
            <h1 className="cm-h1">Healthcare that <span className="text-[#4a90d9]">starts with understanding you.</span></h1>
            <p className="cm-body mt-5 max-w-xl">Answer a few gentle questions and we&rsquo;ll guide you to the right verified doctor &mdash; general or specialist &mdash; ready to see you online, in minutes.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/patient/intake" className="cm-btn-primary" data-testid="hero-find-care-cta">
                <Sparkles size={16}/> Find the right care
              </Link>
              <Link to="/doctors" className="cm-btn-outline" data-testid="hero-know-doctor-cta">
                I know which doctor I want <ArrowRight size={16}/>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-[#475569]">
              <div className="flex items-center gap-1.5"><Check size={14} className="text-[#3daa6e]"/> GMC-verified doctors</div>
              <div className="flex items-center gap-1.5"><Check size={14} className="text-[#3daa6e]"/> Secure video</div>
              <div className="flex items-center gap-1.5"><Check size={14} className="text-[#3daa6e]"/> Consult in minutes</div>
            </div>
          </div>
          <div className="lg:col-span-6 cm-fade-up">
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-[#e2e8f0]">
              <img src={HERO_IMG} alt="Doctor with patient" className="w-full h-[440px] object-cover" data-testid="hero-image"/>
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur border border-[#e2e8f0] rounded-xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#3daa6e]/15 text-[#3daa6e] flex items-center justify-center"><Shield size={18}/></div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#3daa6e] font-semibold">Verified network</div>
                    <div className="text-sm font-semibold text-[#1a2b4a]">Every doctor. Checked, credentialed, and insured.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency notice */}
      <section className="cm-container -mt-4 mb-4">
        <EmergencyBanner />
      </section>

      {/* How it works */}
      <section className="cm-section">
        <div className="cm-container">
          <SectionEyebrow>How CAREMatch Works</SectionEyebrow>
          <h2 className="cm-h2 max-w-2xl">Five gentle steps between you and the right doctor.</h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            <Step n="1" title="Tell us what's happening" desc="Our AI health navigator asks a few plain-English questions." />
            <Step n="2" title="We suggest a route" desc="A general GP or a specialist &mdash; based on what you've shared." />
            <Step n="3" title="Pick your doctor" desc="Browse verified doctors, availability and languages." />
            <Step n="4" title="Book & pay securely" desc="Choose a time and pay at checkout. Fair, transparent pricing." />
            <Step n="5" title="Meet on video" desc="Secure video consult, notes saved to your Care Passport." />
          </div>
        </div>
      </section>

      {/* Find a doctor */}
      <section className="cm-section bg-[#f5f7fa]">
        <div className="cm-container grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionEyebrow>Find a Doctor</SectionEyebrow>
            <h2 className="cm-h2 mb-4">Search by specialty, language, availability &mdash; on your terms.</h2>
            <p className="cm-body mb-6">Filter our verified network by consultation type, gender preference, spoken languages and price. Every profile shows credentials, experience and next available slot.</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {["Cardiology","Dermatology","ENT","Orthopaedics","Gynaecology","Neurology","Gastroenterology","Endocrinology","Urology","Ophthalmology","Psychiatry"].map(s => (
                <span key={s} className="cm-chip" data-testid={`specialty-chip-${s.toLowerCase()}`}>{s}</span>
              ))}
            </div>
            <Link to="/find-care" className="cm-btn-primary" data-testid="home-search-cta">Search doctors <ArrowRight size={16}/></Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="cm-card"><Users className="text-[#4a90d9] mb-2"/><div className="text-3xl font-bold text-[#1a2b4a]">4,200+</div><div className="text-sm text-[#475569]">consultations</div></div>
            <div className="cm-card mt-8"><Stethoscope className="text-[#3daa6e] mb-2"/><div className="text-3xl font-bold text-[#1a2b4a]">340+</div><div className="text-sm text-[#475569]">verified doctors</div></div>
            <div className="cm-card"><HeartPulse className="text-[#4a90d9] mb-2"/><div className="text-3xl font-bold text-[#1a2b4a]">11</div><div className="text-sm text-[#475569]">specialties</div></div>
            <div className="cm-card mt-8"><MessageCircle className="text-[#3daa6e] mb-2"/><div className="text-3xl font-bold text-[#1a2b4a]">18 min</div><div className="text-sm text-[#475569]">avg. time to consult</div></div>
          </div>
        </div>
      </section>

      {/* AI Nav */}
      <section className="cm-section">
        <div className="cm-container grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="rounded-2xl border border-[#e2e8f0] bg-white p-6 shadow-lg">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#4a90d9] font-semibold mb-4"><Sparkles size={14}/> AI-assisted, not AI-diagnosed</div>
              <div className="space-y-3 text-sm">
                <div className="bg-[#f5f7fa] rounded-lg p-3 text-[#475569]">What can we help you with today?</div>
                <div className="bg-[#ebf3fc] rounded-lg p-3 text-[#1a2b4a] font-medium ml-8">A tight, painful headache for 3 days.</div>
                <div className="bg-[#f5f7fa] rounded-lg p-3 text-[#475569]">Thanks. Is the pain on one side, both, or moving?</div>
                <div className="bg-[#ebf3fc] rounded-lg p-3 text-[#1a2b4a] font-medium ml-8">Both sides, worse in the afternoon.</div>
                <div className="rounded-lg border border-[#3daa6e]/30 bg-[#eaf7f0] p-3 text-sm">
                  <div className="font-semibold text-[#1a2b4a]">Suggested: General Practice</div>
                  <div className="text-[#475569] mt-1">Based on what you've shared, a GP consultation looks like the right first step.</div>
                </div>
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <SectionEyebrow>AI-assisted healthcare navigation</SectionEyebrow>
            <h2 className="cm-h2 mb-4">Not a diagnosis. A better starting point.</h2>
            <p className="cm-body mb-4">Our AI navigator asks the right questions and points you toward the right kind of care. A qualified clinician always makes the final call.</p>
            <ul className="space-y-2 text-sm text-[#475569]">
              <li className="flex items-start gap-2"><Check size={16} className="text-[#3daa6e] mt-0.5 flex-shrink-0"/> Plain-English questions, no medical jargon</li>
              <li className="flex items-start gap-2"><Check size={16} className="text-[#3daa6e] mt-0.5 flex-shrink-0"/> Recognises red-flag symptoms and pauses to redirect you</li>
              <li className="flex items-start gap-2"><Check size={16} className="text-[#3daa6e] mt-0.5 flex-shrink-0"/> Every summary reviewed by your doctor before your appointment</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Consultation types */}
      <section className="cm-section bg-[#f5f7fa]">
        <div className="cm-container">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="cm-card" data-testid="pricing-general-card">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#4a90d9] font-semibold mb-3"><Stethoscope size={14}/> General Consultation</div>
              <div className="text-2xl font-semibold text-[#1a2b4a]">Everyday health, sorted</div>
              <p className="text-sm text-[#475569] mt-2">GP consultation for everyday health concerns &mdash; coughs, colds, minor infections, repeat prescriptions.</p>
              <ul className="mt-4 space-y-2 text-sm text-[#475569]">
                <li className="flex items-center gap-2"><Check size={14} className="text-[#3daa6e]"/> 15-minute video consult</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#3daa6e]"/> Prescription where clinically appropriate</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#3daa6e]"/> Notes to your Care Passport</li>
              </ul>
            </div>
            <div className="cm-card border-[#4a90d9]/40" data-testid="pricing-specialist-card">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#4a90d9] font-semibold mb-3"><HeartPulse size={14}/> Specialist Consultation</div>
              <div className="text-2xl font-semibold text-[#1a2b4a]">Direct access to consultants</div>
              <p className="text-sm text-[#475569] mt-2">Direct access to consultants across 10+ specialties &mdash; from cardiology to dermatology.</p>
              <ul className="mt-4 space-y-2 text-sm text-[#475569]">
                <li className="flex items-center gap-2"><Check size={14} className="text-[#3daa6e]"/> 20-minute video consult</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#3daa6e]"/> Referrals and diagnostic pathways</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-[#3daa6e]"/> Follow-up plan included</li>
              </ul>
            </div>
          </div>
          <p className="text-center text-xs text-[#94a3b8] mt-4">Fair, transparent pricing shown at checkout &mdash; no surprises.</p>
        </div>
      </section>

      {/* Feature grid */}
      <section className="cm-section">
        <div className="cm-container">
          <SectionEyebrow>Built with care</SectionEyebrow>
          <h2 className="cm-h2 max-w-2xl mb-10">Small details that make a real difference.</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Feature icon={<Shield size={20}/>} title="Verified Healthcare Professionals" desc="Every doctor is checked against GMC and specialty registers before joining." testid="feature-verified"/>
            <Feature icon={<FileText size={20}/>} title="Prepare before your appointment" desc="We share your intake summary with the doctor so you don't have to repeat yourself." testid="feature-prep"/>
            <Feature icon={<Video size={20}/>} title="Secure Video Consultations" desc="End-to-end encrypted video with a friction-free pre-check for camera and mic." testid="feature-video"/>
            <Feature icon={<FolderHeart size={20}/>} title="Care Passport" desc="A living record of consultations, notes, medications and referrals &mdash; owned by you." testid="feature-passport"/>
            <Feature icon={<CalendarCheck size={20}/>} title="Follow-up care" desc="Book follow-ups with the same doctor. Continuity built in." testid="feature-followup"/>
            <Feature icon={<CircleDollarSign size={20}/>} title="Transparent pricing" desc="Know what you'll pay before you book. No surprise fees, ever." testid="feature-pricing"/>
          </div>
        </div>
      </section>

      {/* For doctors */}
      <section className="cm-section bg-[#1a2b4a] text-white">
        <div className="cm-container grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-[#4a90d9] font-semibold mb-3">For Doctors</div>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Focus on care. We'll handle the rest.</h2>
            <p className="text-white/70 mb-6 leading-relaxed">Set your availability, review AI-assisted patient briefs, and consult from anywhere. Weekly payouts, transparent per-consultation earnings.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/register/doctor" className="cm-btn-accent" data-testid="home-join-doctor-cta">Join as a doctor <ArrowRight size={16}/></Link>
              <Link to="/for-doctors" className="inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold border border-white/20 text-white hover:bg-white/5" data-testid="home-doctor-learn-more">Learn more</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl bg-white/5 border border-white/10 p-5"><div className="text-xs uppercase tracking-wider text-[#4a90d9]">AI-assisted briefs</div><div className="text-lg font-semibold mt-1">Ready before every consult</div></div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-5"><div className="text-xs uppercase tracking-wider text-[#4a90d9]">Weekly payouts</div><div className="text-lg font-semibold mt-1">Reliable, on schedule</div></div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-5 col-span-2"><div className="text-xs uppercase tracking-wider text-[#4a90d9]">You get</div><div className="text-lg font-semibold mt-1">AI-assisted patient briefs, weekly recurring slots, automated payouts, and a dashboard designed by clinicians.</div></div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="cm-section">
        <div className="cm-container">
          <SectionEyebrow>Trust & Safety</SectionEyebrow>
          <h2 className="cm-h2 max-w-2xl mb-10">Built on the foundations you'd expect.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Feature icon={<Shield size={20}/>} title="Doctor verification" desc="GMC checks, specialty registers, professional indemnity and ID." testid="trust-verify"/>
            <Feature icon={<FolderHeart size={20}/>} title="Data privacy" desc="Encrypted at rest and in transit. You own your Care Passport." testid="trust-privacy"/>
            <Feature icon={<Sparkles size={20}/>} title="AI transparency" desc="Every AI-generated summary is labelled. Clinicians verify before use." testid="trust-ai"/>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="cm-section bg-[#f5f7fa]">
        <div className="cm-container">
          <SectionEyebrow>FAQ</SectionEyebrow>
          <h2 className="cm-h2 mb-8">Questions people usually ask.</h2>
          <div className="space-y-3">
            <FAQItem idx="0" q="Is CAREMatch a replacement for A&E?" a="No. CAREMatch is not for medical emergencies. If you think you're experiencing one, call 999 (UK) or your local emergency number."/>
            <FAQItem idx="1" q="Who are the doctors on the platform?" a="All doctors are verified against the GMC (or equivalent) register with valid professional indemnity."/>
            <FAQItem idx="2" q="How does the AI navigator work?" a="It asks plain-English questions and suggests the right route to care. It never diagnoses. Your doctor always makes the final clinical decision."/>
            <FAQItem idx="3" q="What do consultations cost?" a="Fair, transparent pricing that we show clearly at checkout before you confirm. No surprise fees."/>
            <FAQItem idx="4" q="Can I get a prescription?" a="Yes, where clinically appropriate. Your doctor decides based on the consultation."/>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cm-section">
        <div className="cm-container">
          <div className="rounded-2xl bg-gradient-to-br from-[#1a2b4a] to-[#4a90d9] text-white p-10 lg:p-16 text-center">
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4">Ready to find the right care?</h2>
            <p className="text-white/80 max-w-xl mx-auto mb-8">Answer a few questions and we'll take it from there.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Link to="/register" className="cm-btn-accent bg-white text-[#1a2b4a] hover:bg-white/90" data-testid="cta-signup">Create a free account</Link>
              <Link to="/doctors" className="inline-flex items-center gap-2 rounded-lg px-5 py-3 text-sm font-semibold border border-white/30 text-white hover:bg-white/10" data-testid="cta-browse">Browse doctors</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
